import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  slackQueryKeys,
  useStartSlackOAuth,
} from "@/shared/domain/slack/hooks";

import { SLACK_OAUTH_MESSAGES } from "./slackOAuthMessages";

/**
 * How far the install has progressed. `starting` is the API call that mints the authorize
 * URL; `waiting` is the popup sitting on Slack's consent screen; `abandoned` is a popup
 * dismissed before it reported anything. A failure carries its cause rather than its copy,
 * which belongs to the page.
 */
export type SlackConnectStatus =
  | { phase: "idle" | "starting" | "waiting" | "abandoned" }
  /** The popup reported a failure, with the reason it sent. */
  | { phase: "failed"; reason: "popup"; message: string }
  /** The request that mints the authorize URL failed, so no popup ever reached Slack. */
  | { phase: "failed"; reason: "start"; error: unknown };

export interface SlackConnection {
  /**
   * Starts the install. Call this straight from a click handler — it opens the popup
   * synchronously, and browsers only allow that under transient activation.
   */
  connect: () => void;
  status: SlackConnectStatus;
  /** Clears a `failed` or `abandoned` status without starting a new attempt. */
  dismiss: () => void;
}

/**
 * Owns the Slack install popup: opening it, following it to either reported outcome, and
 * noticing when it is dismissed without reporting one.
 *
 * The popup lands on `SlackOAuthCallback`, which posts `SLACK_OAUTH_MESSAGES` back here.
 * Success invalidates the connection list, which is how the page learns a workspace appeared.
 */
export function useConnectSlackPopup(orgId: string): SlackConnection {
  const queryClient = useQueryClient();
  const startOAuthMutation = useStartSlackOAuth(orgId);
  const startOAuth = startOAuthMutation.mutate;
  const resetStartOAuth = startOAuthMutation.reset;

  const popupRef = React.useRef<Window | null>(null);
  // The org this attempt started as. Switching orgs while the popup is open must not
  // invalidate a list the workspace did not land in.
  const installOrgRef = React.useRef(orgId);
  // Latches the attempt's outcome: whoever reports first wins, so the poll below can never
  // relabel a reported install as abandoned.
  const reportedRef = React.useRef(false);
  // Whether a start request is still the only thing that can give the popup a URL.
  const startInFlightRef = React.useRef(false);

  const [phase, setPhase] = React.useState<"idle" | "waiting" | "abandoned">(
    "idle",
  );
  // Once the popup has reported here, closing it does not lose the reason.
  const [popupError, setPopupError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const handler = (
      event: MessageEvent<{ type?: string; message?: string }>,
    ) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const popup = popupRef.current;
      if (popup === null) {
        return;
      }
      // The callback page posts and then closes itself, and a discarded browsing context is
      // delivered as a null source — so while our popup is outstanding, a null source is
      // taken as its report. A source that is some other live window never is.
      if (event.source !== null && event.source !== popup) {
        return;
      }

      const type = event.data?.type;
      if (
        type !== SLACK_OAUTH_MESSAGES.success &&
        type !== SLACK_OAUTH_MESSAGES.error
      ) {
        return;
      }

      popupRef.current = null;
      reportedRef.current = true;
      startInFlightRef.current = false;
      setPhase("idle");

      if (type === SLACK_OAUTH_MESSAGES.success) {
        setPopupError(null);
        void queryClient.invalidateQueries({
          queryKey: slackQueryKeys.connections(installOrgRef.current),
        });
      } else {
        setPopupError(
          event.data.message ?? "The Slack popup reported a failure.",
        );
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [queryClient]);

  // A popup dismissed mid-flow never reports, so the page would otherwise sit unchanged.
  React.useEffect(() => {
    if (phase !== "waiting") {
      return;
    }
    const timer = window.setInterval(() => {
      const popup = popupRef.current;
      if (reportedRef.current || popup === null) {
        return;
      }
      if (popup.closed) {
        popupRef.current = null;
        setPhase("abandoned");
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [phase]);

  React.useEffect(() => {
    return () => {
      // TanStack Query drops a mutation's per-call callbacks once the caller unmounts, so an
      // in-flight start leaves nobody to give this popup a URL or to close it.
      if (startInFlightRef.current) {
        popupRef.current?.close();
        popupRef.current = null;
      }
    };
  }, []);

  const connect = React.useCallback(() => {
    setPopupError(null);
    setPhase("idle");
    reportedRef.current = false;
    startInFlightRef.current = true;
    installOrgRef.current = orgId;
    // Opened synchronously in the click handler: popups are only allowed under transient
    // activation, which Safari revokes ~1s after the gesture — awaiting the API first loses
    // the popup whenever the backend is slow. The URL is assigned once it arrives.
    const popup = window.open(
      "",
      "slack-oauth",
      "width=600,height=800,popup=yes",
    );
    popupRef.current = popup;
    startOAuth(undefined, {
      onSuccess: ({ authorization_url }) => {
        startInFlightRef.current = false;
        if (popup) {
          popup.location.href = authorization_url;
          setPhase("waiting");
        } else {
          // Popup blocked: fall back to navigating this window.
          window.location.href = authorization_url;
        }
      },
      onError: () => {
        // Surfaced through the mutation's error state, below.
        startInFlightRef.current = false;
        popup?.close();
        popupRef.current = null;
        setPhase("idle");
      },
    });
  }, [orgId, startOAuth]);

  const dismiss = React.useCallback(() => {
    setPopupError(null);
    setPhase("idle");
    resetStartOAuth();
  }, [resetStartOAuth]);

  const status = React.useMemo<SlackConnectStatus>(() => {
    if (popupError !== null) {
      return { phase: "failed", reason: "popup", message: popupError };
    }
    if (startOAuthMutation.isError) {
      return {
        phase: "failed",
        reason: "start",
        error: startOAuthMutation.error,
      };
    }
    if (startOAuthMutation.isPending) {
      return { phase: "starting" };
    }
    return { phase };
  }, [
    phase,
    popupError,
    startOAuthMutation.error,
    startOAuthMutation.isError,
    startOAuthMutation.isPending,
  ]);

  return { connect, status, dismiss };
}
